import { useState } from 'react';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormSuccess(true);
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const urlParams = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt' && value) {
          urlParams.append(key, value.toString());
        }
      });

      const resp = await fetch('https://readdy.ai/api/form/d9srsdl8o1vn02ekulag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlParams.toString(),
      });

      const responseText = await resp.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (resp.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(msg);
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const aboutSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于日本伦理d3tt',
      description: '日本伦理d3tt官方资讯平台 - 了解我们的品牌故事、信誉保障与联系方式',
      url: 'https://jp-ethics-d3tt.readdy.site/about',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jp-ethics-d3tt.readdy.site/' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '日本伦理d3tt',
      url: 'https://jp-ethics-d3tt.readdy.site/',
      description: '日本伦理d3tt官方资讯平台，提供最新最全的日本伦理d3tt相关资讯与资源介绍',
      foundingDate: '2024',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@jp-ethics-d3tt.readdy.site',
        availableLanguage: ['Chinese'],
      },
    },
  ];

  const stats = [
    { value: '50万+', label: '月活跃用户' },
    { value: '1万+', label: '内容条目' },
    { value: '99.9%', label: '服务可用率' },
    { value: '4.8', label: '用户评分' },
  ];

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: '内容品质保证',
      desc: '所有日本伦理d3tt内容均经过严格审核，确保品质与安全，为用户提供最可靠的资源推荐。',
    },
    {
      icon: 'ri-flashlight-line',
      title: '实时更新',
      desc: '日本伦理d3tt内容每日更新，紧跟行业动态，确保用户第一时间获取最新资讯与资源。',
    },
    {
      icon: 'ri-user-heart-line',
      title: '用户至上',
      desc: '日本伦理d3tt始终以用户体验为核心，持续优化平台功能与内容质量，满足用户的多元需求。',
    },
    {
      icon: 'ri-global-line',
      title: '全平台覆盖',
      desc: '日本伦理d3tt支持桌面端和移动端访问，无论何时何地都能畅享流畅的浏览体验。',
    },
  ];

  return (
    <>
      <SEO
        title="关于我们 - 日本伦理d3tt官方资讯平台"
        description="了解日本伦理d3tt的品牌故事、发展历程与信誉保障。日本伦理d3tt致力于为用户提供最优质的资讯与资源服务，欢迎联系我们。"
        keywords="日本伦理d3tt,关于日本伦理d3tt,日本伦理d3tt联系方式"
        canonicalUrl="https://jp-ethics-d3tt.readdy.site/about"
        ogType="website"
        schemaData={aboutSchema}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />

        <main className="flex-1 pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero */}
          <section className="w-full bg-background-100 py-14 md:py-20">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto text-center">
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">
                关于日本伦理<span className="text-primary-500">d3tt</span>
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                日本伦理d3tt是专注于相关资讯与资源整合的内容平台。自成立以来，我们始终秉持品质优先、用户至上的理念，致力于为全球华人用户提供最新、最全、最可靠的日本伦理d3tt相关信息与服务。
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="w-full bg-background-50 py-10 md:py-14">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                    <p className="text-2xl md:text-3xl font-bold text-primary-500 font-heading mb-1">{stat.value}</p>
                    <p className="text-xs text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Brand Story */}
          <section className="w-full bg-background-50 py-12 md:py-16">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="text-xs font-semibold text-primary-500 uppercase tracking-wider mb-2 inline-block">
                    品牌故事
                  </span>
                  <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                    专注日本伦理d3tt资讯，服务全球用户
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    日本伦理d3tt诞生于用户对高品质、便捷化内容获取的需求。我们的创始团队深耕内容领域多年，深刻理解用户在寻找日本伦理d3tt相关资源时面临的痛点 —— 信息分散、质量参差不齐、获取渠道不明确。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    为此，我们打造了日本伦理d3tt这个一站式内容资讯平台，通过系统化的分类、严格的品质审核和用户友好的界面设计，让每一位用户都能快速、便捷地找到所需的日本伦理d3tt内容。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    如今，日本伦理d3tt已成为该领域领先的资讯平台，月活跃用户超过50万。我们将继续努力，不断提升内容质量与服务水平。
                  </p>
                </div>
                <div className="w-full h-80 rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20professional%20team%20collaboration%20in%20a%20warm%20light%20filled%20workspace%2C%20creative%20office%20environment%2C%20elegant%20interior%20design%20with%20terracotta%20and%20cream%20accents%2C%20team%20discussing%20around%20a%20table%2C%20warm%20atmosphere%2C%20editorial%20photography%20style%2C%20soft%20natural%20lighting&width=800&height=600&seq=about-brand-story&orientation=landscape"
                    alt="日本伦理d3tt品牌故事"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="w-full bg-background-100 py-12 md:py-16">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-2">
                  为什么选择日本伦理d3tt
                </h2>
                <p className="text-sm text-foreground-500">我们的核心优势</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {features.map((feat) => (
                  <div key={feat.title} className="flex gap-4 p-5 bg-background-50 rounded-lg border border-background-200/70">
                    <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-accent-100 flex items-center justify-center">
                      <i className={`${feat.icon} text-lg text-accent-600`} />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-1.5">{feat.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{feat.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="w-full bg-background-50 py-12 md:py-16">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-2">
                  信誉保障
                </h2>
                <p className="text-sm text-foreground-500">用户对我们的信任是我们最宝贵的资产</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  { title: '内容审核体系', desc: '日本伦理d3tt建立了严格的三级内容审核机制，确保平台上所有资源都经过专业团队的审查，杜绝低质量和违规内容。' },
                  { title: '用户隐私保护', desc: '日本伦理d3tt采用业界领先的加密技术保护用户数据，严格遵守隐私保护法规，绝不泄露用户个人信息。' },
                  { title: '持续服务承诺', desc: '日本伦理d3tt拥有专业的运维团队，7×24小时保障平台稳定运行，为用户提供持续可靠的访问服务。' },
                ].map((item) => (
                  <div key={item.title} className="p-5 rounded-lg border border-background-200/70 bg-background-100 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-verified-badge-line text-xl text-secondary-600" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full bg-background-100 py-12 md:py-16">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    如果您对日本伦理d3tt有任何疑问、建议或合作意向，欢迎通过以下方式联系我们。我们的团队将在收到信息后尽快回复。
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-primary-100 flex items-center justify-center">
                        <i className="ri-mail-line text-primary-600" />
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400 mb-0.5">电子邮箱</p>
                        <p className="text-sm text-foreground-800 font-medium">contact@jp-ethics-d3tt.readdy.site</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-accent-100 flex items-center justify-center">
                        <i className="ri-customer-service-2-line text-accent-600" />
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400 mb-0.5">客服支持</p>
                        <p className="text-sm text-foreground-800 font-medium">工作日 9:00 - 18:00</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-secondary-100 flex items-center justify-center">
                        <i className="ri-map-pin-line text-secondary-600" />
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400 mb-0.5">办公地址</p>
                        <p className="text-sm text-foreground-800 font-medium">东京都千代田区丸之内1-1-1</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                    <h3 className="text-base font-bold font-heading text-foreground-900 mb-4">发送消息</h3>

                    {formSuccess ? (
                      <div className="text-center py-8">
                        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                          <i className="ri-check-line text-2xl text-accent-600" />
                        </div>
                        <p className="text-foreground-800 font-medium">消息已发送！</p>
                        <p className="text-sm text-foreground-500 mt-1">感谢您的来信，我们将尽快回复</p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
                        <div>
                          <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                          <input
                            id="about-name"
                            name="name"
                            type="text"
                            required
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                          <input
                            id="about-email"
                            name="email"
                            type="email"
                            required
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                        <div>
                          <label htmlFor="about-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                          <input
                            id="about-subject"
                            name="subject"
                            type="text"
                            required
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                            placeholder="请输入消息主题"
                          />
                        </div>
                        <div>
                          <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1.5">消息内容</label>
                          <textarea
                            id="about-message"
                            name="message"
                            required
                            maxLength={500}
                            rows={3}
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                            placeholder="请输入您的消息内容（不超过500字）"
                          />
                        </div>
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                        {formError && (
                          <p className="text-xs text-red-500">{formError}</p>
                        )}
                        <button
                          type="submit"
                          disabled={submitting}
                          className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {submitting ? (
                            <span className="flex items-center justify-center gap-2">
                              <i className="ri-loader-4-line animate-spin" />
                              发送中...
                            </span>
                          ) : (
                            '发送消息'
                          )}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}